import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/redux/store";
import { useMutation, useQuery } from "@tanstack/react-query";

// MUI
import { Grid, Typography, Skeleton } from "@mui/material";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// API
import { getLandingPages } from "@/User/api/UserPreferences/queries";
import { updateUser } from "@/User/api/UserPreferences/mutations";

// Redux
import { actions } from "@/redux/reducers/User";
import { IUser } from "@/Common/interfaces/user";
import { errorLogger } from "@/Common/helpers/logger";
import { ILandingPage } from "@/User/types/UserPreferences/LandingPage";

export default function LandingPage() {
	const { user } = useSelector((state: RootState) => state?.user);
	const selectedLandingPage = user?.preferences?.landingPage ?? "";
	const [localSelectedLandingPage, setLocalSelectedLandingPage] = useState(selectedLandingPage);
	const dispatch = useDispatch();
	const { mutate, isPending } = useMutation({
		mutationFn: (preferences: IUser) => updateUser(user?._id, user?.locationId, preferences),
	});

	const landingPages = async () => {
		return getLandingPages();
	};
	const { data, status: queryStatus } = useQuery({ queryKey: ["userPreferenceLandingPages"], queryFn: landingPages });

	const changingLadingPage = (value: ILandingPage) => {
		mutate(
			{
				preferences: {
					landingPage: value?.route || "",
					landingLocation: user?.preferences?.landingLocation,
				},
			},
			{
				onSuccess: (res) => {
					if (!res) {
						return;
					}
					dispatch(
						actions.SET_USER({
							user: {
								...user,
								preferences: res.preferences,
							},
						})
					);
					// Update local state after successful mutation
					setLocalSelectedLandingPage(res?.preferences?.landingPage ?? "");
				},
				onError: (err) => errorLogger(err, { function: "changingLadingPage", message: "Failed to change landing page." }),
			}
		);
	};

	useEffect(() => {
		if (data) {
			// Update local selected landing page when data is fetched
			setLocalSelectedLandingPage(selectedLandingPage);
		}
	}, [data, selectedLandingPage]);

	return (
		<Grid id="2" item className="scrollable-content">
			<Typography className="user-preferences-section-text">Landing Page</Typography>
			{isPending || queryStatus === "pending" ? (
				<Skeleton
					sx={{
						borderRadius: "4px",
					}}
					variant="rectangular"
					width={240}
					height={30}
				/>
			) : (
				<SmartSelector
					showBy="pageName"
					selectBy="route"
					options={data?.sort((item1: ILandingPage, item2: ILandingPage) => (item1.pageName > item2.pageName ? 1 : -1)) || []}
					selected={localSelectedLandingPage}
					inputProps={{
						placeholder: "Select Landing Page",
					}}
					clearable
					sx={{ width: "240px" }}
					onChangeAction={(value) => changingLadingPage(value as ILandingPage)}
				/>
			)}
		</Grid>
	);
}
